// Inventory: Cost (Starchive-Inventory-Section-Plan, section 3.2; the canvas's I3 board). Checks the costs that exist. The
// intake is the only source of cost from now on; the 593 legacy pieces stay "cost unknown by design" (Tim, 29 Sep) and are
// never asked for a number. Keep marks a typed cost as known-after-the-fact (it still does not count in profit); Clear
// blanks the stray number in Shopify. Both owner-signed and audited.
import { useState } from 'react'
import { Screen, useMeta } from '../ReportsApp'
import { downloadCsv, reportsAction, useReport } from '../api'
import { consignorName, int, money, n } from '../format'
import { AsyncButton, Badge, Button, Card, Note, Tile } from '../ui'
import { OwnerChip, PhotoImg, type FloorPiece } from './inventory-ui'
import { PieceDrawer } from './PieceDrawer'

type CostRow = FloorPiece & { cost_decision: string | null; cost_decided_by: string | null }
interface CostData {
  tiles: { real_cost: number; real_cost_sum: number; real_list_sum: number; real_matching: number; legacy: number; consignors: number
    to_check: number; typed: number; typed_undecided: number; kept: number; consignor_with_cost: number; zero: number }
  typed: CostRow[]; consignor_cost: CostRow[]; intake: CostRow[]; above_list: CostRow[]
}

export function Cost() {
  const { meta } = useMeta()
  const { data: d, error, loading, refresh } = useReport<CostData>('inv_cost')
  const [ownerId, setOwnerId] = useState('')
  const [open, setOpen] = useState<string | null>(null)
  const [msg, setMsg] = useState('')
  const owner = ownerId.length === 4 ? (meta?.owners ?? []).find((o) => o.employee_id === ownerId) : undefined

  const act = async (action: string, productId: string) => {
    setMsg('')
    try { await reportsAction(action, { product_id: productId, employee_id: ownerId }); refresh() }
    catch (e) { setMsg((e as Error).message) }
  }
  const t = d?.tiles
  const needsId = <span className="rp-muted" style={{ fontSize: 12 }}>Enter an owner ID (top right) to decide</span>

  return (
    <Screen title="Cost" sub="Checks the costs that exist. The intake is the only source of cost from now on"
      right={<>
        <label className="rp-ownerbar"><span>Owner ID</span>
          <input inputMode="numeric" autoComplete="off" value={ownerId} onChange={(e) => setOwnerId(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))} placeholder="••••" aria-label="Owner ID" />
          {ownerId.length === 4 && <Badge tone={owner ? 'green' : 'red'}>{owner ? owner.name : 'Not an owner'}</Badge>}
        </label>
        <AsyncButton run={() => downloadCsv('floor')} doneLabel="Downloaded">Export CSV</AsyncButton>
      </>}>
      {error && <div className="rp-error">{error}</div>}
      {msg && <div className="rp-error">{msg}</div>}
      <div className="rp-banner"><b>Cost comes from the intake.</b> Pieces listed before the app have no intake and stay <b>cost unknown by design</b> (Tim, 29 Sep): nothing here asks for a cost on them. This tab only checks the costs that exist.</div>
      <div className="rp-grid rp-grid--tiles">
        <Tile label="Real cost, from the intake" tone="green" value={t ? int(t.real_cost) : '…'} sub={t ? `${money(t.real_cost_sum, true)} cost on ${money(t.real_list_sum, true)} at list · ${t.real_matching === t.real_cost ? 'all match Shopify' : `${int(t.real_cost - t.real_matching)} differ from Shopify`}` : ''} />
        <Tile label="Legacy, cost unknown" value={t ? int(t.legacy) : '…'} sub="Listed before the app. Profit on these shows as unknown, never a guess" />
        <Tile label="Consignors' pieces" value={t ? int(t.consignors) : '…'} sub="No cost, by rule" />
        <Tile label="To look at" tone={t && t.to_check ? 'amber' : 'plain'} value={t ? int(t.to_check) : '…'} sub={t ? `${int(t.typed_undecided)} typed costs waiting on Keep or Clear · ${int(t.consignor_with_cost)} consignor ${t.consignor_with_cost === 1 ? 'piece' : 'pieces'} with a cost` : ''} />
      </div>
      <div className="rp-grid rp-grid--2">
        <Card title="Costs typed into Shopify on legacy pieces" tone="amber"
          note={<>Someone entered these by hand before the app. <b>Keep</b> marks one as a known cost, entered after the fact (told apart from intake costs, still not counted in profit). <b>Clear</b> blanks the number in Shopify and the piece goes back to unknown.</>}>
          <div className="rp-table" role="table">
            <div className="rp-table__head" style={{ gridTemplateColumns: 'minmax(0, 2fr) 100px 80px 70px 54px 150px' }} role="row">
              {['Piece', 'Whose', 'Typed', 'List', 'Age', 'Decide'].map((h, i) => <span key={h} role="columnheader" className={[2, 3, 4].includes(i) ? 'r' : ''}>{h}</span>)}
            </div>
            {(d?.typed ?? []).map((r) => (
              <div key={r.product_id} className="rp-table__row" style={{ gridTemplateColumns: 'minmax(0, 2fr) 100px 80px 70px 54px 150px' }} role="row">
                <span role="cell" className="rp-pcell" style={{ cursor: 'pointer' }} onClick={() => setOpen(r.product_id)}><PhotoImg url={r.image_url} title={r.title} width={96} small /><span className="rp-pcell__text"><b>{r.title}</b><span className="rp-sub">{r.brand}{r.size ? ` · Sz ${r.size}` : ''}</span></span></span>
                <span role="cell"><OwnerChip owner={r.owner} consignorKey={r.consignor_key} /></span>
                <span role="cell" className="r"><b>{money(r.shopify_cost, true)}</b></span>
                <span role="cell" className="r">{money(r.list_price, true)}</span>
                <span role="cell" className="r">{int(r.days_on_floor)} d</span>
                <span role="cell">
                  {r.cost_decision === 'keep'
                    ? <Badge tone="green">Kept{r.cost_decided_by ? ` by ${r.cost_decided_by}` : ''}</Badge>
                    : owner
                      ? <span style={{ display: 'flex', gap: 6 }}><AsyncButton small run={() => act('cost_keep', r.product_id)}>Keep</AsyncButton><AsyncButton small run={() => act('cost_clear', r.product_id)}>Clear</AsyncButton></span>
                      : needsId}
                </span>
              </div>
            ))}
            {!loading && (d?.typed ?? []).length === 0 && <div className="rp-table__empty">No typed costs left to decide</div>}
          </div>
        </Card>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {(d?.consignor_cost ?? []).length > 0 && (
            <Card title="A consignor's piece with a cost" tone="red" note="A third-party consignor's piece never carries a cost. If it is really Masih's, fix the owner on the Owners tab; if the number is stray, clear it.">
              {(d?.consignor_cost ?? []).map((r) => (
                <div key={r.product_id} className="rp-costcase">
                  <PhotoImg url={r.image_url} title={r.title} width={240} />
                  <div className="rp-costcase__body">
                    <b style={{ cursor: 'pointer' }} onClick={() => setOpen(r.product_id)}>{r.title}</b>
                    <span className="rp-sub">{r.consignor_key ? `Consignor: ${consignorName(r.consignor_key)}` : 'Consignor, no key on the tag'} · {money(r.list_price, true)} list · {int(r.days_on_floor)} days</span>
                    <span className="rp-costcase__cost">{money(r.shopify_cost, true)} cost in Shopify</span>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {owner ? <AsyncButton small run={() => act('cost_clear', r.product_id)}>Clear the cost</AsyncButton> : needsId}
                      <Button small onClick={() => setOpen(r.product_id)}>Look at the piece</Button>
                    </div>
                  </div>
                </div>
              ))}
            </Card>
          )}
          <Card title="Taken in through the app" tone="green" note="Cost from the intake, copied to Shopify and onto the sale line">
            <div className="rp-table" role="table">
              <div className="rp-table__head" style={{ gridTemplateColumns: 'minmax(0, 2fr) 90px 70px 110px' }} role="row">
                {['Piece', 'Whose', 'Intake', 'Shopify'].map((h, i) => <span key={h} role="columnheader" className={i === 2 ? 'r' : ''}>{h}</span>)}
              </div>
              {(d?.intake ?? []).map((r) => (
                <div key={r.product_id} className="rp-table__row" style={{ gridTemplateColumns: 'minmax(0, 2fr) 90px 70px 110px' }} role="row">
                  <span role="cell" className="rp-pcell" style={{ cursor: 'pointer' }} onClick={() => setOpen(r.product_id)}><PhotoImg url={r.image_url} title={r.title} width={96} small /><span className="rp-pcell__text"><b>{r.title}</b><span className="rp-sub">Intake #{String(r.intake_no ?? '').padStart(4, '0')}</span></span></span>
                  <span role="cell"><OwnerChip owner={r.owner} consignorKey={r.consignor_key} /></span>
                  <span role="cell" className="r">{money(r.cost, true)}</span>
                  <span role="cell">{r.shopify_cost != null && n(r.shopify_cost) === n(r.cost) ? <span className="rp-tone-green" style={{ fontWeight: 700 }}>✓ matches</span>
                    : owner ? <AsyncButton small run={() => act('cost_copy', r.product_id)}>Copy to Shopify</AsyncButton>
                    : <Badge tone="amber">Shopify says {r.shopify_cost == null ? 'nothing' : money(r.shopify_cost, true)}</Badge>}</span>
                </div>
              ))}
            </div>
          </Card>
          {t && t.zero > 0 && (
            <Card title="$0.00 costs in Shopify" tone="grey">
              <Note>{int(t.zero)} pieces have $0.00 as their Shopify cost. Read as unknown (legacy), never as free.</Note>
            </Card>
          )}
        </div>
      </div>
      {open && <PieceDrawer productId={open} onClose={() => setOpen(null)} onChanged={refresh} />}
    </Screen>
  )
}
